<script setup lang="ts">
import { ref } from 'vue';
import { toTitleCase } from '@/functions/toTitleCase';
import TestReviewStatusIcon from '@/components/Student/TestReview/TestReviewStatusIcon.vue';
import { QuestionStatus } from '@/types/TestReview';

const props = defineProps<{
    appliedFilters: string[];
}>();
const filterOptions = Object.values(QuestionStatus);
const selectedFilters = ref<string[]>(props.appliedFilters);
const emit = defineEmits<{(event: 'apply-filters', filters: string[]): void }>();

// Allows the button label to update when filters are applied
const submittedFilters = ref<string[]>(props.appliedFilters);

const submitFilters = () => {
    emit('apply-filters', selectedFilters.value);
    submittedFilters.value = selectedFilters.value;
};

const onCancel = () => {
    selectedFilters.value = submittedFilters.value;
};

</script>
<template>
    <div class="row review-status-filter">
        <q-list
            dense
            :separator="$q.screen.xs"
            class="q-gutter-y-sm q-py-sm col-12"
        >
            <q-item
                v-if="!$q.screen.xs"
                v-close-popup="true"
                class="text-fsum-med-blue clear-all-button"
                clickable
                @click="() => { selectedFilters = []; submitFilters() }"
            >
                <q-item-section :class="{'q-pt-sm': $q.screen.xs }">
                    <q-item-label>Clear All</q-item-label>
                </q-item-section>
            </q-item>
            <q-item
                v-for="status in filterOptions"
                :key="status"
                clickable
            >
                <q-item-section
                    avatar
                    :class="{'q-pt-sm': $q.screen.xs }"
                >
                    <q-checkbox
                        v-model="selectedFilters"
                        class="filter-item"
                        keep-color
                        :val="status"
                        size="sm"
                        dense
                    >
                        {{ toTitleCase(status) }}
                        <TestReviewStatusIcon
                            :label="status"
                            :size="'15px'"
                        />
                    </q-checkbox>
                </q-item-section>
            </q-item>
            <q-separator v-if="$q.screen.xs" />
        </q-list>
    </div>
    <div class="row justify-end button-group q-px-md q-py-md q-gutter-x-sm">
        <q-btn
            v-close-popup="true"
            label="Cancel"
            class="fsum-button-outlined q-px-sm"
            no-caps
            dense
            flat
            @click="onCancel"
        />
        <q-btn
            v-close-popup="true"
            label="Apply"
            class="fsum-button-dark-blue q-px-sm q-mr-md"
            no-caps
            dense
            flat
            @click="() => submitFilters()"
        />
    </div>
</template>
<style scoped lang="scss">
.filter-item {
    font-size: 13px;
}

.clear-all-button {
    font-size: 14px;

    &:hover {
        text-decoration: underline;
        cursor: pointer;
    }
}

:deep(div.clear-all-button:hover > div.q-focus-helper) {
        background: var(--fsum-white);
}

/** Customize the filter button's outline. I had trouble customizing it with the outline prop */
.quiz-filter-button {
    border: 1px solid var(--fsum-gray2);
}

:deep(.q-btn .q-icon) {
    font-size: 18px;
}

/* Change the checkbox color */
:deep(.q-checkbox__bg) {
    border-color: var(--fsum-blue3);
}
:deep(.q-checkbox__inner.q-checkbox__inner--truthy) {
    color: var(--fsum-blue3);
}
</style>
<style lang="scss">
.quiz-filter-button .on-left {
    margin-right: 2px;
}
</style>
